// src/utils/analytics.js

// Configuration
export const ANALYTICS_CONFIG = {
  GA_MEASUREMENT_ID: 'G-P3397JQD95',
  GTM_ID: 'GTM-K4CXBXXH',
  DEBUG: process.env.NODE_ENV === 'development'
};

// Google Tag Manager initialization
export const initGTM = () => {
  if (typeof window === 'undefined' || window.gtmInitialized) return;

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  
  // GTM script injection
  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${ANALYTICS_CONFIG.GTM_ID}`;
  document.head.appendChild(gtmScript);

  // GTM initialization with privacy configuration
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
    // Configure privacy settings
    'ads_data_redaction': true,
    'url_passthrough': true,
    // Disable automatic link decoration that creates "1st Party" text
    'link_decoration': false
  });

  window.gtmInitialized = true;

  if (ANALYTICS_CONFIG.DEBUG) {
    console.log('GTM initialized with ID:', ANALYTICS_CONFIG.GTM_ID);
  }
};

// Enhanced Google Analytics initialization (Legacy - for fallback)
export const initGA = () => {
  if (typeof window === 'undefined') return;

  // Load Google Analytics script
  if (!window.gtag) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ANALYTICS_CONFIG.GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    
    // Configure with enhanced settings
    window.gtag('config', ANALYTICS_CONFIG.GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
      anonymize_ip: true, // GDPR compliance
      allow_google_signals: true,
      allow_ad_personalization_signals: true
    });

    if (ANALYTICS_CONFIG.DEBUG) {
      console.log('GA initialized with ID:', ANALYTICS_CONFIG.GA_MEASUREMENT_ID);
    }
  }
};

// Enhanced page view tracking
export const trackPageView = (path, title = document.title) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', ANALYTICS_CONFIG.GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title,
    page_location: `${window.location.origin}${path}`
  });

  if (ANALYTICS_CONFIG.DEBUG) {
    console.log('Page view tracked:', { path, title });
  }
};

// Enhanced event tracking with custom parameters
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  const defaultParams = {
    event_category: 'engagement',
    event_label: parameters.label || '',
    value: parameters.value || 0
  };

  const eventParams = { ...defaultParams, ...parameters };

  window.gtag('event', eventName, eventParams);

  if (ANALYTICS_CONFIG.DEBUG) {
    console.log('Event tracked:', eventName, eventParams);
  }
};

// Business-specific tracking functions
export const trackBusinessEvents = {
  // Page view tracking
  pageView: (pageName, path = window.location.pathname, title = document.title) => {
    trackPageView(path, title);
    
    trackEvent('page_view', {
      event_category: 'navigation',
      event_label: pageName,
      page_name: pageName,
      page_path: path,
      page_title: title
    });
  },

  // Gallery specific tracking
  galleryImageView: (imageName, category) => {
    trackEvent('gallery_image_view', {
      event_category: 'gallery',
      event_label: imageName,
      image_name: imageName,
      image_category: category,
      value: 1
    });
  },

  filterUsage: (filterType, filterValue) => {
    trackEvent('filter_usage', {
      event_category: 'user_interaction',
      event_label: `${filterType}: ${filterValue}`,
      filter_type: filterType,
      filter_value: filterValue
    });
  },

  mediaInteraction: (action, mediaName) => {
    trackEvent('media_interaction', {
      event_category: 'media',
      event_label: `${action}: ${mediaName}`,
      action: action,
      media_name: mediaName
    });
  },

  userInteraction: (actionType, actionValue) => {
    trackEvent('user_interaction', {
      event_category: 'engagement',
      event_label: `${actionType}: ${actionValue}`,
      action_type: actionType,
      action_value: actionValue
    });
  },

  // Contact tracking
  contactFormSubmit: (formType, source = 'website') => {
    trackEvent('form_submit', {
      event_category: 'contact',
      event_label: formType,
      form_type: formType,
      source: source,
      value: 1
    });
  },

  phoneClick: (phoneNumber, location = 'unknown') => {
    trackEvent('phone_call_initiate', {
      event_category: 'contact',
      event_label: `Phone: ${phoneNumber}`,
      contact_method: 'phone',
      phone_number: phoneNumber,
      click_location: location,
      value: 5
    });
  },

  emailClick: (email, location = 'unknown') => {
    trackEvent('email_initiate', {
      event_category: 'contact',
      event_label: `Email: ${email}`,
      contact_method: 'email',
      email_address: email,
      click_location: location,
      value: 3
    });
  },

  whatsappClick: (location = 'unknown') => {
    trackEvent('whatsapp_initiate', {
      event_category: 'contact',
      event_label: 'WhatsApp Chat',
      contact_method: 'whatsapp',
      click_location: location,
      value: 4
    });
  },

  // Service interest tracking
  serviceInterest: (serviceType, action = 'view') => {
    trackEvent('service_interest', {
      event_category: 'services',
      event_label: serviceType,
      service_type: serviceType,
      action: action,
      value: action === 'inquiry' ? 10 : 2
    });
  },

  trainingInterest: (trainingType, action = 'view') => {
    trackEvent('training_interest', {
      event_category: 'training',
      event_label: trainingType,
      training_type: trainingType,
      action: action,
      value: action === 'booking' ? 15 : 3
    });
  },

  // Content engagement
  blogEngagement: (articleTitle, action, scrollDepth = null) => {
    const params = {
      event_category: 'content',
      event_label: articleTitle,
      content_type: 'blog',
      article_title: articleTitle,
      action: action
    };

    if (scrollDepth) {
      params.scroll_depth = scrollDepth;
    }

    trackEvent('blog_engagement', params);
  },

  downloadBrochure: (brochureType) => {
    trackEvent('brochure_download', {
      event_category: 'lead_generation',
      event_label: brochureType,
      content_type: 'brochure',
      brochure_type: brochureType,
      value: 8
    });
  },

  // Social media tracking
  socialMediaClick: (platform, location = 'unknown') => {
    trackEvent('social_media_click', {
      event_category: 'social',
      event_label: platform,
      social_platform: platform,
      click_location: location,
      value: 1
    });
  },

  // Navigation tracking
  menuNavigation: (menuItem, menuType = 'main') => {
    trackEvent('menu_navigation', {
      event_category: 'navigation',
      event_label: menuItem,
      menu_type: menuType,
      menu_item: menuItem
    });
  },

  // External link tracking
  externalLinkClick: (url, linkText) => {
    trackEvent('external_link_click', {
      event_category: 'outbound_links',
      event_label: linkText,
      external_url: url,
      link_text: linkText
    });
  },

  // Conversion tracking
  leadGeneration: (source, method, value = 20) => {
    trackEvent('generate_lead', {
      event_category: 'conversions',
      event_label: `${source} - ${method}`,
      source: source,
      method: method,
      value: value
    });
  },

  consultationRequest: (serviceType, contactMethod) => {
    trackEvent('consultation_request', {
      event_category: 'conversions',
      event_label: serviceType,
      service_type: serviceType,
      contact_method: contactMethod,
      value: 25
    });
  },

  // Performance tracking
  performanceEvent: (eventName, data = {}) => {
    trackEvent('performance_event', {
      event_category: 'performance',
      event_label: eventName,
      performance_event: eventName,
      ...data
    });
  },

  // Component loading tracking
  componentLoad: (componentName, loadTime) => {
    trackEvent('component_load', {
      event_category: 'performance',
      event_label: componentName,
      component_name: componentName,
      load_time: loadTime,
      value: Math.round(loadTime)
    });
  },

  // Error tracking
  errorEvent: (errorType, errorMessage, componentName = 'unknown') => {
    trackEvent('error_event', {
      event_category: 'errors',
      event_label: errorType,
      error_type: errorType,
      error_message: errorMessage,
      component_name: componentName
    });
  }
};

// Scroll depth tracking
export const initScrollTracking = () => {
  if (typeof window === 'undefined') return;

  let scrollDepths = [25, 50, 75, 90];
  let trackedDepths = new Set();

  const trackScrollDepth = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
    );

    scrollDepths.forEach(depth => {
      if (scrollPercent >= depth && !trackedDepths.has(depth)) {
        trackedDepths.add(depth);
        trackEvent('scroll_depth', {
          event_category: 'engagement',
          event_label: `${depth}%`,
          scroll_depth: depth,
          page_path: window.location.pathname
        });
      }
    });
  };

  window.addEventListener('scroll', trackScrollDepth, { passive: true });
  
  return () => {
    window.removeEventListener('scroll', trackScrollDepth);
  };
};

// Time on page tracking
export const initTimeTracking = () => {
  if (typeof window === 'undefined') return;

  const startTime = Date.now();
  let timeIntervals = [30, 60, 120, 300]; // seconds
  let trackedIntervals = new Set();

  const trackTimeOnPage = () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);

    timeIntervals.forEach(interval => {
      if (timeSpent >= interval && !trackedIntervals.has(interval)) {
        trackedIntervals.add(interval);
        trackEvent('time_on_page', {
          event_category: 'engagement',
          event_label: `${interval}s`,
          time_spent: interval,
          page_path: window.location.pathname
        });
      }
    });
  };

  const intervalId = setInterval(trackTimeOnPage, 10000); // Check every 10 seconds

  return () => {
    clearInterval(intervalId);
  };
};

// Form tracking
export const trackFormInteraction = (formName, fieldName, action) => {
  trackEvent('form_interaction', {
    event_category: 'forms',
    event_label: `${formName} - ${fieldName}`,
    form_name: formName,
    field_name: fieldName,
    action: action
  });
};

// Error tracking
export const trackError = (errorType, errorMessage, location) => {
  trackEvent('exception', {
    event_category: 'errors',
    event_label: errorType,
    description: errorMessage,
    location: location,
    fatal: false
  });
};

// Performance tracking
export const trackPerformance = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  window.addEventListener('load', () => {
    setTimeout(() => {
      const perfData = window.performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      const domReadyTime = perfData.domContentLoadedEventEnd - perfData.navigationStart;

      trackEvent('page_performance', {
        event_category: 'performance',
        event_label: window.location.pathname,
        page_load_time: pageLoadTime,
        dom_ready_time: domReadyTime,
        value: pageLoadTime
      });
    }, 0);
  });
};

// Enhanced SEO tracking
export const trackSEOMetrics = (source, medium, campaign) => {
  const urlParams = new URLSearchParams(window.location.search);
  
  const utmParams = {
    source: urlParams.get('utm_source') || source || 'direct',
    medium: urlParams.get('utm_medium') || medium || 'none',
    campaign: urlParams.get('utm_campaign') || campaign || 'none',
    term: urlParams.get('utm_term') || '',
    content: urlParams.get('utm_content') || ''
  };

  trackEvent('traffic_source', {
    event_category: 'acquisition',
    event_label: `${utmParams.source} / ${utmParams.medium}`,
    ...utmParams
  });
};

// Initialize enhanced analytics
export const initEnhancedAnalytics = () => {
  // Initialize GTM (which will handle GA4)
  initGTM();
  
  // Initialize additional tracking
  initScrollTracking();
  initTimeTracking();
  trackPerformance();
  trackSEOMetrics();
  
  if (ANALYTICS_CONFIG.DEBUG) {
    console.log('Enhanced Analytics initialized with GTM');
  }
};